/**
 * Vendors Page - vendor-first provider onboarding.
 */

import {
  Alert, Box, Button, Chip, CircularProgress, Collapse,
  IconButton, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LinkIcon from '@mui/icons-material/Link';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SyncIcon from '@mui/icons-material/Sync';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import {
  useDeleteVendor,
  useLiveTest,
  useSyncThirdPartyConfig,
  useThirdPartyConfigSyncStatus,
  useUpdateVendor,
  useVendorList,
} from '../hooks/useVendors';
import type { LiveTestResult, VendorEndpointMapping, VendorMapping } from '../domain/vendor';
import VendorModal from '../components/VendorModal';
import { AdminListPageHeader } from '../components/admin/adminListPageUi';
import {
  discardVendorOnboardingDraft,
  findLatestVendorOnboardingDraft,
  isOnboardingDraftActionable,
  pruneCorruptOnboardingDrafts,
  pruneObsoleteOnboardingDrafts,
  resolveDraftVendorIdForResume,
  VENDOR_ONBOARDING_DRAFT_STORAGE_KEY,
  VENDOR_ONBOARDING_STEP_LABELS,
  type VendorOnboardingDraftMeta,
} from '../components/vendorOnboardingWizardState';
import { ProviderListLabelFilter } from '../components/adapter/ProviderListLabelFilter';
import {
  normalizeConfigLabel,
  resolveVendorAdapterConfigLabel,
  vendorAddApiContext,
  vendorAddApiDisabledMessage,
  vendorMatchesAdapterConfigLabel,
} from '../lib/adapterCapabilityCatalog';
import { useAdapterServiceCatalog } from '../hooks/useAdapterServices';
import { removeEndpointMapping } from '../lib/endpointMappingUtils';
import { PermissionKey } from '@c4ytechnology/authz';
import { hasAdminPermission } from '../lib/adminPermissions';
import { useAdminConsoleMe } from '../hooks/useAdminConsoleMe';
import { vendorTestErrorMessage } from '../lib/vendorTestErrors';

const AUTH_LABELS: Record<string, string> = {
  none: 'None',
  'api-key': 'API Key',
  bearer: 'Bearer',
  basic: 'Basic',
  'oauth-client-credentials': 'OAuth Client',
};

const AUTH_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning'> = {
  none: 'default',
  'api-key': 'primary',
  bearer: 'primary',
  basic: 'secondary',
  'oauth-client-credentials': 'success',
};

function vendorDisplayName(vendor: VendorMapping): string {
  return vendor.vendorName?.trim() || vendor.vendorId;
}

/** Show vendorId subline only when it adds info beyond the display name (case-insensitive). */
function shouldShowVendorIdLine(vendor: VendorMapping): boolean {
  const id = vendor.vendorId.trim();
  if (!id) return false;
  return id.toLowerCase() !== vendorDisplayName(vendor).trim().toLowerCase();
}

function endpointKey(vendorId: string, endpoint: VendorEndpointMapping): string {
  return `${vendorId}:${endpoint.serviceName ?? ''}:${endpoint.capability}`;
}

function endpointUrl(vendor: VendorMapping, endpoint: VendorEndpointMapping): string | null {
  if (endpoint.testUrl) return endpoint.testUrl;
  if (!vendor.baseUrl || !endpoint.requestPath) return null;
  return `${vendor.baseUrl.replace(/\/$/, '')}/${endpoint.requestPath.replace(/^\//, '')}`;
}

function endpointFieldCount(endpoint: VendorEndpointMapping): number {
  return endpoint.responseFields?.length ?? Object.keys(endpoint.responseMapping ?? {}).length;
}

function isEndpointDraft(endpoint: VendorEndpointMapping): boolean {
  return endpoint.onboardingStatus === 'draft';
}

function isEndpointPublished(endpoint: VendorEndpointMapping): boolean {
  return !isEndpointDraft(endpoint);
}

const NESTED_API_TABLE_HEAD_SX = {
  '& .MuiTableCell-root': { fontWeight: 700 },
  bgcolor: 'action.hover',
} as const;

const NESTED_API_COL = {
  service: { width: 140 },
  api: { minWidth: 180 },
  endpoint: { minWidth: 200 },
  enabled: { width: 100 },
  mapped: { width: 120 },
  actions: { whiteSpace: 'nowrap' as const },
} as const;

const VENDOR_TOOLBAR_CONTROL_HEIGHT = 40;

const vendorToolbarButtonSx = {
  height: VENDOR_TOOLBAR_CONTROL_HEIGHT,
  minHeight: VENDOR_TOOLBAR_CONTROL_HEIGHT,
  minWidth: 140,
  whiteSpace: 'nowrap',
} as const;

function serviceLabels(vendor: VendorMapping): string[] {
  return Array.from(new Set((vendor.endpointMappings ?? [])
    .map(endpoint => endpoint.serviceName)
    .filter((value): value is string => Boolean(value))));
}

export const Vendors = () => {
  const { data, isLoading, isError } = useVendorList();
  const { data: catalogItems = [] } = useAdapterServiceCatalog({ publishedOnly: true });
  const syncStatus = useThirdPartyConfigSyncStatus();
  const [deleteJsonExportWarning, setDeleteJsonExportWarning] = useState<string | null>(null);
  const deleteVendor = useDeleteVendor(undefined, (msg) => setDeleteJsonExportWarning(msg));
  const updateVendor = useUpdateVendor(undefined, undefined, (msg) => setDeleteJsonExportWarning(msg));
  const [deletingDraftKey, setDeletingDraftKey] = useState<string | null>(null);
  const syncThirdPartyConfig = useSyncThirdPartyConfig(undefined, (msg) => setDeleteJsonExportWarning(msg));
  const liveTest = useLiveTest();
  // The live-test endpoint requires vendors.live_test; without it the Test buttons are hidden.
  const canLiveTest = hasAdminPermission(useAdminConsoleMe().data, PermissionKey.VENDORS_LIVE_TEST);
  const [syncConfigLabel, setSyncConfigLabel] = useState('');

  const [testResults, setTestResults] = useState<Record<string, LiveTestResult | null>>({});
  const [testingId, setTestingId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [editVendor, setEditVendor] = useState<VendorMapping | null>(null);
  const [modalMode, setModalMode] = useState<'vendor' | 'add-api' | 'edit-api'>('vendor');
  const [modalEndpoint, setModalEndpoint] = useState<VendorEndpointMapping | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [onboardingDraft, setOnboardingDraft] = useState<VendorOnboardingDraftMeta | null>(null);
  const [resumeDraftKey, setResumeDraftKey] = useState<string | null>(null);

  const filteredVendors = useMemo(
    () =>
      (data ?? []).filter(vendor =>
        vendorMatchesAdapterConfigLabel(vendor, syncConfigLabel, catalogItems),
      ),
    [data, syncConfigLabel, catalogItems],
  );

  const refreshOnboardingDraft = useCallback(() => {
    pruneCorruptOnboardingDrafts();
    if (data?.length) pruneObsoleteOnboardingDrafts(data);
    const latest = findLatestVendorOnboardingDraft();
    setOnboardingDraft(
      latest && isOnboardingDraftActionable(latest, data) ? latest : null,
    );
  }, [data]);

  useEffect(() => {
    refreshOnboardingDraft();
    const onStorage = (event: StorageEvent) => {
      if (event.key === VENDOR_ONBOARDING_DRAFT_STORAGE_KEY) {
        refreshOnboardingDraft();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refreshOnboardingDraft]);

  const openVendorModal = (
    vendor: VendorMapping | null,
    mode: 'vendor' | 'add-api' | 'edit-api',
    endpoint: VendorEndpointMapping | null = null,
    draftKey: string | null = null,
  ) => {
    setResumeDraftKey(draftKey);
    setEditVendor(vendor);
    setModalMode(mode);
    setModalEndpoint(endpoint);
    setOpen(true);
  };

  const handleModalClose = () => {
    setOpen(false);
    setEditVendor(null);
    setModalEndpoint(null);
    setModalMode('vendor');
    setResumeDraftKey(null);
    refreshOnboardingDraft();
  };

  const handleResumeOnboarding = () => {
    if (!onboardingDraft) return;
    const resumeVendorId = resolveDraftVendorIdForResume(onboardingDraft);
    const vendor = resumeVendorId
      ? data?.find(item => item.vendorId === resumeVendorId) ?? null
      : null;
    let endpoint: VendorEndpointMapping | null = null;
    if (onboardingDraft.mode === 'edit-api' && onboardingDraft.endpointCapability && vendor) {
      endpoint = vendor.endpointMappings?.find(
        item => item.capability === onboardingDraft.endpointCapability,
      ) ?? null;
    }
    openVendorModal(vendor, onboardingDraft.mode, endpoint, onboardingDraft.draftKey);
  };

  const handleDeleteOnboardingDraft = () => {
    if (!onboardingDraft) return;
    const label = onboardingDraft.vendorName.trim() || onboardingDraft.vendorId || 'this vendor';
    if (!window.confirm(`Delete the in-progress onboarding draft for ${label}?`)) return;
    discardVendorOnboardingDraft(onboardingDraft);
    refreshOnboardingDraft();
  };

  const handleDeleteDraftEndpoint = (vendor: VendorMapping, endpoint: VendorEndpointMapping) => {
    const key = endpointKey(vendor.vendorId, endpoint);
    if (!window.confirm(`Delete draft API ${endpoint.capability}?`)) return;
    setDeletingDraftKey(key);
    const endpointMappings = removeEndpointMapping(vendor.endpointMappings ?? [], endpoint);
    const effectiveLabel = resolveVendorAdapterConfigLabel(vendor, catalogItems);
    updateVendor.mutate(
      {
        vendorId: vendor.vendorId,
        request: {
          vendorName: vendorDisplayName(vendor),
          baseUrl: vendor.baseUrl ?? '',
          authType: vendor.authType ?? 'none',
          authConfig: vendor.authConfig ?? {},
          endpointMappings,
          ...(effectiveLabel ? { adapterConfigLabel: normalizeConfigLabel(effectiveLabel) } : {}),
        },
      },
      {
        onSettled: () => setDeletingDraftKey(null),
        onSuccess: () => {
          if (
            open &&
            editVendor?.vendorId === vendor.vendorId &&
            modalEndpoint?.capability === endpoint.capability
          ) {
            handleModalClose();
          }
        },
      },
    );
  };

  useEffect(() => {
    if (!data?.length) return;
    setExpanded(prev => {
      let changed = false;
      const next = { ...prev };
      for (const vendor of data) {
        if ((vendor.endpointMappings?.length ?? 0) > 0 && next[vendor.vendorId] === undefined) {
          next[vendor.vendorId] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [data]);

  const handleTest = async (vendor: VendorMapping, endpoint: VendorEndpointMapping) => {
    const url = endpointUrl(vendor, endpoint);
    if (!url) return;
    const key = endpointKey(vendor.vendorId, endpoint);
    setTestingId(key);
    setTestResults(prev => ({ ...prev, [key]: null }));
    liveTest.mutate({
      url,
      method: endpoint.testMethod ?? endpoint.requestMethod ?? 'GET',
      headers: endpoint.requestHeaders ?? {},
      body: endpoint.requestBody ?? undefined,
      bodyType: endpoint.requestBodyType,
      vendorId: vendor.vendorId,
      capability: endpoint.capability,
    }, {
      onSettled: () => setTestingId(null),
      onSuccess: (result) => setTestResults(prev => ({ ...prev, [key]: result })),
      onError: (err: unknown) =>
        setTestResults(prev => ({
          ...prev,
          [key]: {
            statusCode: 0,
            statusText: 'Error',
            ok: false,
            durationMs: 0,
            error: vendorTestErrorMessage(err, 'Request failed'),
            responseBody: null,
          },
        })),
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Failed to load vendor mappings. Ensure admin-api is running with PostgreSQL, then sign in under Admin console
          (sidebar) and verify <code>VITE_ADMIN_API_BASE</code> points at the API if not using the default.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <AdminListPageHeader
        title="Provider Onboarding"
        subtitle="Manage providers as vendors, then expand a vendor to view or add its registered APIs."
      />
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        alignItems={{ lg: 'flex-start' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {syncStatus.data ? (
              <Tooltip
                title={
                  syncStatus.data.lastSuccessAt
                    ? `Last synced ${new Date(syncStatus.data.lastSuccessAt).toLocaleString()}`
                    : 'Adapter config has not been synced yet'
                }
              >
                <Chip
                  label={
                    syncStatus.data.status === 'success'
                      ? 'Adapter synced'
                      : syncStatus.data.status === 'failed'
                        ? 'Sync failed'
                        : 'Not synced'
                  }
                  size="small"
                  color={syncStatus.data.status === 'success' ? 'success' : syncStatus.data.status === 'failed' ? 'warning' : 'default'}
                  variant="outlined"
                />
              </Tooltip>
            ) : null}
            <Chip
              label={`${filteredVendors.length} vendor${filteredVendors.length !== 1 ? 's' : ''}`}
              size="small"
              title={
                filteredVendors.length !== (data?.length ?? 0)
                  ? `${filteredVendors.length} of ${data?.length ?? 0} vendors for label "${syncConfigLabel}"`
                  : undefined
              }
            />
          </Stack>
        </Box>

        <Box sx={{ width: { xs: '100%', lg: 'auto' }, flexShrink: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'minmax(200px, 240px) minmax(140px, 1fr) minmax(160px, 1fr)',
              },
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <ProviderListLabelFilter
              value={syncConfigLabel}
              onChange={setSyncConfigLabel}
              showHelperText={false}
              size="small"
            />
            <Tooltip
              title={
                syncConfigLabel.trim()
                  ? `Sync adapter config for label "${normalizeConfigLabel(syncConfigLabel)}"`
                  : 'Select a specific adapter config label to sync'
              }
              disableInteractive
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={vendorToolbarButtonSx}
                  startIcon={syncThirdPartyConfig.isPending ? <CircularProgress size={14} /> : <SyncIcon />}
                  onClick={() => {
                    setDeleteJsonExportWarning(null);
                    syncThirdPartyConfig.mutate(normalizeConfigLabel(syncConfigLabel.trim()));
                  }}
                  disabled={syncThirdPartyConfig.isPending || !syncConfigLabel.trim()}
                >
                  Sync config
                </Button>
              </span>
            </Tooltip>
            <Button
              id="add-vendor-btn"
              variant="contained"
              size="small"
              fullWidth
              sx={vendorToolbarButtonSx}
              startIcon={<AddIcon />}
              onClick={() => openVendorModal(null, 'vendor')}
            >
              Onboard Vendor
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
            Filter vendors by label; same label is used when syncing adapter config.
          </Typography>
        </Box>
      </Stack>

      {deleteJsonExportWarning ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setDeleteJsonExportWarning(null)}>
          The database change was saved, but a downstream adapter sync step failed: {deleteJsonExportWarning}
        </Alert>
      ) : null}

      {!open && onboardingDraft ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <Button color="inherit" size="small" onClick={handleResumeOnboarding}>
                Resume
              </Button>
              <Button color="inherit" size="small" onClick={handleDeleteOnboardingDraft}>
                Delete draft
              </Button>
            </Box>
          }
        >
          Unfinished provider onboarding for{' '}
          <strong>{onboardingDraft.vendorName.trim() || onboardingDraft.vendorId || 'new vendor'}</strong>
          {' '}— step {onboardingDraft.step + 1} of {VENDOR_ONBOARDING_STEP_LABELS.length} (
          {VENDOR_ONBOARDING_STEP_LABELS[onboardingDraft.step] ?? 'Connect'}).
        </Alert>
      ) : null}

      {syncThirdPartyConfig.isSuccess && syncThirdPartyConfig.data.ok ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Adapter config synced to label &quot;{syncThirdPartyConfig.data.label}&quot; for{' '}
          {syncThirdPartyConfig.data.vendorCount} vendor{syncThirdPartyConfig.data.vendorCount === 1 ? '' : 's'}.
        </Alert>
      ) : null}

      {!isLoading && syncConfigLabel.trim() && (data?.length ?? 0) > 0 && filteredVendors.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No vendors use adapter config label &quot;{normalizeConfigLabel(syncConfigLabel)}&quot;. Choose another label,
          select All labels, or onboard a vendor under this label.
        </Alert>
      ) : null}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 700 } }}>
              <TableCell sx={{ width: 48 }} />
              <TableCell>Vendor</TableCell>
              <TableCell>Services</TableCell>
              <TableCell>Auth</TableCell>
              <TableCell>Registered APIs</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                  No vendors onboarded yet.{' '}
                  <Button size="small" onClick={() => openVendorModal(null, 'vendor')}>
                    Onboard your first vendor
                  </Button>
                </TableCell>
              </TableRow>
            )}

            {syncConfigLabel.trim() && (data?.length ?? 0) > 0 && filteredVendors.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                  No vendors match label &quot;{normalizeConfigLabel(syncConfigLabel)}&quot;.
                </TableCell>
              </TableRow>
            )}

            {filteredVendors.map(vendor => {
              const authLabel = AUTH_LABELS[vendor.authType ?? 'none'] ?? 'None';
              const authColor = AUTH_COLORS[vendor.authType ?? 'none'] ?? 'default';
              const endpoints = vendor.endpointMappings ?? [];
              const services = serviceLabels(vendor);
              const publishedEndpoints = endpoints.filter(isEndpointPublished);
              const draftEndpoints = endpoints.filter(isEndpointDraft);
              const enabledCount = publishedEndpoints.filter(endpoint => endpoint.enabled !== false).length;
              const statusLabel =
                endpoints.length === 0
                  ? 'No APIs'
                  : draftEndpoints.length > 0
                    ? `${enabledCount}/${publishedEndpoints.length} enabled · ${draftEndpoints.length} draft`
                    : `${enabledCount}/${endpoints.length} enabled`;
              const isExpanded = expanded[vendor.vendorId] === true;
              const effectiveLabel = resolveVendorAdapterConfigLabel(vendor, catalogItems);
              const labelInferred = Boolean(effectiveLabel && !vendor.adapterConfigLabel?.trim());
              const addApiContext = vendorAddApiContext(vendor, catalogItems);
              const apiChipLabel = addApiContext.total > 0 && endpoints.length >= addApiContext.total
                ? `${endpoints.length}/${addApiContext.total} APIs`
                : `${endpoints.length} API${endpoints.length === 1 ? '' : 's'}`;
              const addApiTooltip = addApiContext.canAdd
                ? 'Configure another capability for this vendor'
                : vendorAddApiDisabledMessage(addApiContext);

              return (
                <Fragment key={vendor.vendorId}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => setExpanded(prev => ({ ...prev, [vendor.vendorId]: !isExpanded }))}
                        disabled={endpoints.length === 0}
                      >
                        {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {vendorDisplayName(vendor)}
                      </Typography>
                      {shouldShowVendorIdLine(vendor) ? (
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {vendor.vendorId}
                        </Typography>
                      ) : null}
                      {effectiveLabel ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                          <code>{effectiveLabel}</code>
                          {labelInferred ? ' (inferred)' : ''}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {services.length > 0
                          ? services.map(service => <Chip key={service} label={service} size="small" variant="outlined" />)
                          : <Typography variant="caption" color="text.disabled">No services</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {vendor.authType && vendor.authType !== 'none'
                          ? <LockIcon sx={{ fontSize: 13, color: 'primary.main' }} />
                          : <LockOpenIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                        }
                        <Chip
                          label={authLabel}
                          size="small"
                          color={authColor}
                          variant={vendor.authType && vendor.authType !== 'none' ? 'filled' : 'outlined'}
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={apiChipLabel}
                        size="small"
                        color={endpoints.length > 0 ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabel}
                        size="small"
                        color={
                          endpoints.length === 0
                            ? 'default'
                            : draftEndpoints.length > 0
                              ? 'warning'
                              : enabledCount > 0
                                ? 'success'
                                : 'warning'
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={addApiTooltip}>
                        <span>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => openVendorModal(vendor, 'add-api')}
                            disabled={!addApiContext.canAdd}
                            sx={{ mr: 0.5 }}
                          >
                            Add API
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        size="small"
                        onClick={() => openVendorModal(vendor, 'vendor')}
                        sx={{ mr: 0.5 }}
                      >
                        Edit Vendor
                      </Button>
                      <Button
                        id={`delete-vendor-${vendor.vendorId}`}
                        size="small"
                        color="error"
                        onClick={() => {
                          setDeleteJsonExportWarning(null);
                          deleteVendor.mutate(vendor.vendorId);
                        }}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                              Registered APIs
                            </Typography>
                            <Tooltip title={addApiTooltip}>
                              <span>
                                <Button
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => openVendorModal(vendor, 'add-api')}
                                  disabled={!addApiContext.canAdd}
                                >
                                  Add API
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>

                          {endpoints.length === 0 ? (
                            <Alert severity="info">No APIs registered for this vendor yet.</Alert>
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={NESTED_API_TABLE_HEAD_SX}>
                                  <TableCell sx={NESTED_API_COL.service}>Service</TableCell>
                                  <TableCell sx={NESTED_API_COL.api}>API</TableCell>
                                  <TableCell sx={NESTED_API_COL.endpoint}>Endpoint</TableCell>
                                  <TableCell sx={NESTED_API_COL.enabled}>Enabled</TableCell>
                                  <TableCell sx={NESTED_API_COL.mapped}>Mapped fields</TableCell>
                                  <TableCell sx={NESTED_API_COL.actions}>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {endpoints.map(endpoint => {
                                  const key = endpointKey(vendor.vendorId, endpoint);
                                  const tr = testResults[key];
                                  const isTesting = testingId === key;
                                  const url = endpointUrl(vendor, endpoint);
                                  const draft = isEndpointDraft(endpoint);

                                  return (
                                    <Fragment key={key}>
                                      <TableRow hover>
                                        <TableCell sx={NESTED_API_COL.service}>{endpoint.serviceName ?? '-'}</TableCell>
                                        <TableCell sx={NESTED_API_COL.api}>
                                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                                            {endpoint.capability}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={NESTED_API_COL.endpoint}>
                                          {url ? (
                                            <Tooltip title={url} arrow>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 300 }}>
                                                <LinkIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                                <Typography variant="caption" sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                  {(endpoint.requestMethod ?? 'GET')} {endpoint.requestPath || '/'}
                                                </Typography>
                                              </Box>
                                            </Tooltip>
                                          ) : (
                                            <Typography variant="caption" color="text.disabled">Not set</Typography>
                                          )}
                                        </TableCell>
                                        <TableCell sx={NESTED_API_COL.enabled}>
                                          <Chip
                                            size="small"
                                            color={
                                              draft
                                                ? 'warning'
                                                : endpoint.enabled === false
                                                  ? 'default'
                                                  : 'success'
                                            }
                                            variant="outlined"
                                            label={
                                              draft
                                                ? 'Draft'
                                                : endpoint.enabled === false
                                                  ? 'Disabled'
                                                  : 'Enabled'
                                            }
                                          />
                                        </TableCell>
                                        <TableCell sx={NESTED_API_COL.mapped}>
                                          <Chip
                                            size="small"
                                            variant="outlined"
                                            label={`${endpointFieldCount(endpoint)} field${endpointFieldCount(endpoint) === 1 ? '' : 's'}`}
                                          />
                                        </TableCell>
                                        <TableCell sx={NESTED_API_COL.actions}>
                                          {!draft && canLiveTest ? (
                                            <Button
                                              size="small"
                                              color="info"
                                              startIcon={isTesting ? <CircularProgress size={12} /> : <PlayArrowIcon />}
                                              onClick={() => handleTest(vendor, endpoint)}
                                              disabled={isTesting || !url}
                                              sx={{ mr: 0.5 }}
                                            >
                                              Test
                                            </Button>
                                          ) : null}
                                          {draft ? (
                                            <>
                                              <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => openVendorModal(vendor, 'edit-api', endpoint)}
                                                sx={{ mr: 0.5 }}
                                              >
                                                Continue
                                              </Button>
                                              <Button
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteDraftEndpoint(vendor, endpoint)}
                                                disabled={deletingDraftKey === key}
                                              >
                                                {deletingDraftKey === key ? (
                                                  <CircularProgress size={12} />
                                                ) : (
                                                  'Delete'
                                                )}
                                              </Button>
                                            </>
                                          ) : (
                                            <Button size="small" onClick={() => openVendorModal(vendor, 'edit-api', endpoint)}>
                                              Edit API
                                            </Button>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                      {tr && (
                                        <TableRow>
                                          <TableCell colSpan={7} sx={{ py: 0.5, px: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                                              {tr.ok
                                                ? <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                                : <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                              }
                                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                {tr.ok
                                                  ? `HTTP ${tr.statusCode} ${tr.statusText} - ${tr.durationMs}ms`
                                                  : tr.statusCode
                                                    ? `HTTP ${tr.statusCode} ${tr.statusText} - ${tr.durationMs}ms`
                                                    : `${tr.error ?? 'Connection failed'} - ${tr.durationMs}ms`
                                                }
                                              </Typography>
                                              {tr.auth?.error && (
                                                <Typography variant="caption" color="error.main" sx={{ fontFamily: 'monospace' }}>
                                                  Auth: {tr.auth.error}
                                                </Typography>
                                              )}
                                              <Button
                                                size="small"
                                                sx={{ ml: 'auto', fontSize: 11, minWidth: 0 }}
                                                onClick={() => setTestResults(prev => ({ ...prev, [key]: null }))}
                                              >
                                                Dismiss
                                              </Button>
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </Fragment>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <VendorModal
        open={open}
        onClose={handleModalClose}
        existing={editVendor}
        mode={modalMode}
        endpoint={modalEndpoint}
        resumeDraftKey={resumeDraftKey}
      />
    </Box>
  );
};
